import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { getSupportTicket, updateSupportTicketStatus } from "@/lib/support.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/support/$code")({
  head: ({ params }) => ({
    meta: [
      { title: `Ticket ${params.code} — The Hospitality Reserve` },
      { name: "description", content: "Support ticket details, status timeline, and attachments." },
    ],
  }),
  component: TicketDetail,
});

type Payload = Awaited<ReturnType<typeof getSupportTicket>>;

function fmt(d: string | null) {
  if (!d) return "—";
  try { return new Date(d).toLocaleString(); } catch { return d; }
}

function statusVariant(s: string): "default" | "secondary" | "outline" {
  if (s === "resolved") return "default";
  if (s === "reviewed") return "secondary";
  return "outline";
}

function TicketDetail() {
  const { code } = Route.useParams();
  const navigate = useNavigate();
  const fetchTicket = useServerFn(getSupportTicket);
  const updateStatus = useServerFn(updateSupportTicketStatus);
  const [data, setData] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [saving, setSaving] = useState(false);

  async function reload() {
    setLoading(true);
    try {
      const res = await fetchTicket({ data: { ticketCode: code } });
      setData(res);
      setAdminNotes(res.ticket.admin_notes ?? "");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    // check admin role for optional status controls
    supabase.auth.getUser().then(async ({ data: u }) => {
      if (!u.user) return;
      const { data: role } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", u.user.id)
        .eq("role", "admin")
        .maybeSingle();
      setIsAdmin(Boolean(role));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  async function setStatus(status: "received" | "reviewed" | "resolved") {
    setSaving(true);
    try {
      await updateStatus({ data: { ticketCode: code, status, adminNotes: adminNotes || null } });
      toast.success(`Marked ${status}`);
      await reload();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <div className="max-w-3xl mx-auto p-6">
        <Link to="/support" className="text-xs tracking-wide-luxury text-muted-foreground hover:text-foreground transition-colors">
          ← All tickets
        </Link>

        {loading ? (
          <p className="mt-6 text-sm text-muted-foreground">Loading…</p>
        ) : error ? (
          <p className="mt-6 text-sm text-destructive">{error}</p>
        ) : !data ? null : (
          <>
            <div className="mt-2 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs text-muted-foreground font-mono">{data.ticket.ticket_code}</div>
                <h1 className="font-display text-2xl">{data.ticket.subject}</h1>
              </div>
              <Badge variant={statusVariant(data.ticket.status)} className="capitalize">
                {data.ticket.status}
              </Badge>
            </div>

            <div className="mt-6 rounded-lg border border-border/60 p-4 text-sm whitespace-pre-wrap">
              {data.ticket.message}
            </div>

            <section className="mt-6">
              <h2 className="text-sm font-medium mb-2">Status timeline</h2>
              <ol className="space-y-1.5 text-sm text-muted-foreground">
                <li>• Received — {fmt(data.ticket.created_at)}</li>
                <li>• Reviewed — {fmt(data.ticket.reviewed_at)}</li>
                <li>• Resolved — {fmt(data.ticket.resolved_at)}</li>
              </ol>
            </section>

            {data.emailEvents.length > 0 && (
              <section className="mt-6">
                <h2 className="text-sm font-medium mb-2">Email delivery</h2>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {data.emailEvents.map((e, i) => (
                    <li key={i}>• {e.status} — {fmt(e.created_at)}</li>
                  ))}
                </ul>
              </section>
            )}

            {data.ticket.shift_id && (
              <section className="mt-6">
                <h2 className="text-sm font-medium mb-2">Linked shift</h2>
                <button
                  className="text-sm underline underline-offset-2"
                  onClick={() =>
                    navigate({ to: "/admin/shifts/$id", params: { id: data.ticket.shift_id! } })
                  }
                >
                  Open shift {data.ticket.shift_id.slice(0, 8)}…
                </button>
              </section>
            )}

            <section className="mt-6">
              <h2 className="text-sm font-medium mb-2">Attachments</h2>
              {data.attachments.length === 0 ? (
                <p className="text-sm text-muted-foreground">None</p>
              ) : (
                <ul className="space-y-1.5 text-sm">
                  {data.attachments.map((a) => (
                    <li key={a.id} className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2">
                      <span className="truncate">
                        {a.file_name}
                        {a.size_bytes ? (
                          <span className="text-muted-foreground"> · {(a.size_bytes / 1024).toFixed(1)} KB</span>
                        ) : null}
                      </span>
                      {a.url ? (
                        <a
                          href={a.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs underline underline-offset-2 shrink-0"
                        >
                          Download
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground">Unavailable</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {isAdmin && (
              <section className="mt-8 rounded-lg border border-border/60 p-4">
                <h2 className="text-sm font-medium mb-2">Admin controls</h2>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Internal notes (visible to admins only)"
                  rows={3}
                  maxLength={2000}
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" disabled={saving} onClick={() => setStatus("received")}>
                    Mark received
                  </Button>
                  <Button size="sm" variant="secondary" disabled={saving} onClick={() => setStatus("reviewed")}>
                    Mark reviewed
                  </Button>
                  <Button size="sm" disabled={saving} onClick={() => setStatus("resolved")}>
                    Mark resolved
                  </Button>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
